import { useState, useEffect } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Code, Globe, Send, Copy, Check, AlertCircle, CheckCircle2, ChevronDown, ChevronRight, Mail, ExternalLink } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function WebsiteTracking({ client, getHeaders }) {
  const [snippet, setSnippet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [copiedEmail, setCopiedEmail] = useState(false);
  const [isFranchise, setIsFranchise] = useState(false);
  const [sendMode, setSendMode] = useState(null); // 'email' | 'copy'
  const [recipientEmail, setRecipientEmail] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [customMessage, setCustomMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    fetch(`${API}/api/client/dashboard/call-tracking/dni-snippet`, { headers: getHeaders() })
      .then(r => r.json())
      .then(d => {
        if (d.error) setError(d.message);
        else setSnippet(d);
      })
      .catch(() => setError('Failed to load tracking snippet'))
      .finally(() => setLoading(false));
  }, [getHeaders]);

  const copySnippet = () => {
    if (!snippet?.snippet) return;
    navigator.clipboard.writeText(snippet.snippet);
    setCopied(true);
    setTimeout(() => setCopied(false), 3000);
  };

  const buildEmailContent = () => {
    if (!snippet) return '';
    return `Hi,

We're implementing call tracking for ${snippet.business_name} to measure which marketing channels are generating phone calls. We need a small JavaScript snippet added to the website.

WHAT THIS SCRIPT DOES:
This uses Dynamic Number Insertion (DNI) — an industry-standard, SEO-safe technique:
• Search engine bots see the real business number (${snippet.real_phone}) in the HTML → preserves SEO
• Human visitors see a tracking number (${snippet.tracking_phone}) → lets us track call sources
• Zero impact on site performance (~1KB, runs after page load)

INSTALLATION:
Paste this script just before the closing </body> tag on all pages:

${snippet.snippet}

SUMMARY:
• Real business number (in HTML source): ${snippet.real_phone}
• Tracking number (shown to visitors): ${snippet.tracking_phone}
• Where to place: Before </body> on all pages (or global footer template)

If you have any questions about this script, please let us know. Thank you!`;
  };

  const copyEmailContent = () => {
    navigator.clipboard.writeText(buildEmailContent());
    setCopiedEmail(true);
    setTimeout(() => setCopiedEmail(false), 3000);
  };

  const sendEmail = async () => {
    if (!recipientEmail) return;
    setSending(true);
    try {
      const res = await fetch(`${API}/api/client/dashboard/call-tracking/dni-send-request`, {
        method: 'POST',
        headers: { ...getHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          recipient_email: recipientEmail,
          recipient_name: recipientName || 'Web Team',
          sender_name: snippet?.business_name || '',
          custom_message: customMessage,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setSent(true);
      } else {
        setError(data.message || 'Failed to send');
      }
    } catch {
      setError('Failed to send email');
    }
    setSending(false);
  };

  if (loading) return null;

  // Don't show anything if no Website tracking number is assigned
  // Website DNI is optional — only show when admin has assigned a Website channel number
  if (error && !snippet) {
    return null;
  }

  if (!snippet) return null;

  return (
    <Card className="border-gray-200 bg-white" data-testid="website-tracking-section">
      <CardContent className="p-0">
        {/* Header */}
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
          data-testid="website-tracking-toggle"
        >
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center">
              <Code className="w-4 h-4 text-indigo-600" />
            </div>
            <div className="text-left">
              <p className="text-sm font-semibold text-gray-900">Website Call Tracking</p>
              <p className="text-xs text-gray-500">SEO-safe dynamic number insertion for your website</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge className="bg-indigo-50 text-indigo-600 text-[10px]">DNI</Badge>
            {expanded ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
          </div>
        </button>

        {expanded && (
          <div className="px-4 pb-5 border-t border-gray-100 pt-4 space-y-4">
            {/* How it works */}
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-xs font-semibold text-gray-700 mb-2">How it works:</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
                <div className="flex items-start gap-2">
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-green-100 text-green-700 text-[10px] font-bold flex items-center justify-center mt-0.5">1</span>
                  <p className="text-gray-600">Your real number <span className="font-mono font-semibold text-gray-900">{snippet.real_phone}</span> stays in the HTML code — search engines see it</p>
                </div>
                <div className="flex items-start gap-2">
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-100 text-blue-700 text-[10px] font-bold flex items-center justify-center mt-0.5">2</span>
                  <p className="text-gray-600">Human visitors see tracking number <span className="font-mono font-semibold text-gray-900">{snippet.tracking_phone}</span> — so we can track the call source</p>
                </div>
                <div className="flex items-start gap-2">
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-purple-100 text-purple-700 text-[10px] font-bold flex items-center justify-center mt-0.5">3</span>
                  <p className="text-gray-600">SEO stays intact, NAP consistency preserved, and call attribution is tracked</p>
                </div>
              </div>
            </div>

            {/* Franchise checkbox */}
            <label className="flex items-center gap-3 cursor-pointer p-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors" data-testid="franchise-checkbox">
              <input
                type="checkbox"
                checked={isFranchise}
                onChange={e => { setIsFranchise(e.target.checked); setSendMode(null); setSent(false); }}
                className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
              <div>
                <p className="text-sm font-medium text-gray-800">Franchise-owned or corporate-managed website?</p>
                <p className="text-xs text-gray-500">If you don't have direct access to your website code, we can help you send the installation request to your web team.</p>
              </div>
            </label>

            {/* Franchise flow */}
            {isFranchise && !sent && (
              <div className="space-y-3 bg-blue-50 rounded-lg p-4 border border-blue-100">
                <p className="text-sm font-medium text-blue-900">How would you like to send the installation request?</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <button
                    onClick={() => setSendMode('email')}
                    data-testid="send-email-option"
                    className={`p-3 rounded-lg border-2 text-left transition-all ${sendMode === 'email' ? 'border-blue-500 bg-white shadow-sm' : 'border-blue-200 bg-white/50 hover:border-blue-300'}`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Send className="w-4 h-4 text-blue-600" />
                      <span className="text-sm font-semibold text-gray-900">Send email request</span>
                    </div>
                    <p className="text-xs text-gray-500">We'll send a professional email with the script and explanation directly to your web team</p>
                  </button>
                  <button
                    onClick={() => setSendMode('copy')}
                    data-testid="copy-info-option"
                    className={`p-3 rounded-lg border-2 text-left transition-all ${sendMode === 'copy' ? 'border-blue-500 bg-white shadow-sm' : 'border-blue-200 bg-white/50 hover:border-blue-300'}`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Copy className="w-4 h-4 text-blue-600" />
                      <span className="text-sm font-semibold text-gray-900">Copy info to send yourself</span>
                    </div>
                    <p className="text-xs text-gray-500">Copy the full explanation + script to paste into your own email to the web team</p>
                  </button>
                </div>

                {/* Send email form */}
                {sendMode === 'email' && (
                  <div className="space-y-3 mt-3 bg-white rounded-lg p-4 border border-gray-200">
                    <div>
                      <label className="text-xs font-medium text-gray-700 mb-1 block">Web team email address *</label>
                      <Input
                        type="email"
                        placeholder="webteam@corporate.com"
                        value={recipientEmail}
                        onChange={e => setRecipientEmail(e.target.value)}
                        data-testid="recipient-email-input"
                        className="text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-700 mb-1 block">Contact name (optional)</label>
                      <Input
                        placeholder="e.g. IT Department, Web Team"
                        value={recipientName}
                        onChange={e => setRecipientName(e.target.value)}
                        className="text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-700 mb-1 block">Add a personal message (optional)</label>
                      <textarea
                        className="w-full rounded-md border border-gray-200 p-2.5 text-sm text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        rows={3}
                        placeholder="e.g. Hi, I'm working with a marketing team and they need this script added to our website for call tracking..."
                        value={customMessage}
                        onChange={e => setCustomMessage(e.target.value)}
                        data-testid="custom-message-input"
                      />
                    </div>
                    <Button
                      onClick={sendEmail}
                      disabled={!recipientEmail || sending}
                      className="bg-blue-600 hover:bg-blue-700 text-white text-sm w-full"
                      data-testid="send-request-btn"
                    >
                      {sending ? 'Sending...' : <><Mail className="w-4 h-4 mr-2" /> Send Installation Request</>}
                    </Button>
                  </div>
                )}

                {/* Copy info */}
                {sendMode === 'copy' && (
                  <div className="mt-3 bg-white rounded-lg p-4 border border-gray-200">
                    <p className="text-xs text-gray-500 mb-2">Click below to copy the full email content (explanation + script) — then paste it into your own email to your web team:</p>
                    <Button
                      onClick={copyEmailContent}
                      className={`w-full text-sm ${copiedEmail ? 'bg-green-600 hover:bg-green-700' : 'bg-indigo-600 hover:bg-indigo-700'} text-white`}
                      data-testid="copy-email-content-btn"
                    >
                      {copiedEmail ? <><Check className="w-4 h-4 mr-2" /> Copied to clipboard!</> : <><Copy className="w-4 h-4 mr-2" /> Copy Full Email Content</>}
                    </Button>
                    <p className="text-xs text-gray-400 mt-2 text-center">Includes: explanation of what DNI does, why it's SEO-safe, and the script with install instructions</p>
                  </div>
                )}
              </div>
            )}

            {/* Sent confirmation */}
            {sent && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3" data-testid="email-sent-confirmation">
                <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-green-800">Installation request sent!</p>
                  <p className="text-xs text-green-700 mt-1">A professional email with the script and explanation has been sent to <strong>{recipientEmail}</strong>. They'll have everything they need to install it.</p>
                  <Button
                    onClick={() => { setSent(false); setSendMode(null); setRecipientEmail(''); }}
                    variant="ghost"
                    size="sm"
                    className="mt-2 text-green-700 hover:text-green-900 text-xs"
                  >
                    Send to another address
                  </Button>
                </div>
              </div>
            )}

            {/* Non-franchise: Direct snippet */}
            {!isFranchise && (
              <div className="space-y-3">
                <p className="text-xs text-gray-600">Paste this script <strong>just before the closing <code className="bg-gray-100 px-1 py-0.5 rounded text-[11px]">&lt;/body&gt;</code> tag</strong> on all pages of your website:</p>
                <div className="relative">
                  <pre className="bg-slate-900 text-slate-200 p-4 rounded-lg text-[11px] font-mono leading-relaxed overflow-x-auto max-h-60" data-testid="dni-snippet-code">
                    {snippet.snippet}
                  </pre>
                  <Button
                    onClick={copySnippet}
                    size="sm"
                    className={`absolute top-2 right-2 text-xs ${copied ? 'bg-green-600 hover:bg-green-700' : 'bg-slate-700 hover:bg-slate-600'} text-white`}
                    data-testid="copy-snippet-btn"
                  >
                    {copied ? <><Check className="w-3 h-3 mr-1" /> Copied</> : <><Copy className="w-3 h-3 mr-1" /> Copy</>}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
